/**
 * Coming back after a reload (DA-3584).
 *
 * Tokens live in memory only (docs/ARCHITECTURE.md), so a reload or a new tab
 * starts signed out — that is the security property, and it stays. What a
 * reload must not do is strand the operator on a sign-in screen and then drop
 * them on `/` instead of the page they were reading. So:
 *
 * - On a deep link, or on `/` in a tab that was signed in, sign-in resumes on
 *   its own. Cognito's hosted-UI session cookie makes that a redirect with no
 *   credential prompt.
 * - The page asked for rides in OIDC `state` and is restored after the
 *   callback, so the operator lands where they were.
 *
 * The marker below is NOT a credential: it records only "this tab was signed
 * in", so a refresh on `/` resumes while a first visit or a sign-out shows the
 * sign-in screen. sessionStorage scopes it to the tab.
 */

const MARKER = "foodics-admin:had-session";

export function hadSession(): boolean {
  try {
    return sessionStorage.getItem(MARKER) === "1";
  } catch {
    return false;
  }
}

export function markHadSession(): void {
  try {
    sessionStorage.setItem(MARKER, "1");
  } catch {
    // Storage blocked: a reload on `/` shows the button. Nothing else breaks.
  }
}

export function clearHadSession(): void {
  try {
    sessionStorage.removeItem(MARKER);
  } catch {
    // As above.
  }
}

/** Whether a signed-out load should start sign-in by itself. */
export function shouldResumeSignIn(path: string, signedInBefore: boolean): boolean {
  if (path.startsWith("/callback")) return false;
  return path !== "/" || signedInBefore;
}

/**
 * The path to restore after the callback, from the OIDC `state` this console
 * wrote. Only a same-origin path is honoured: anything else would make `state`
 * an open redirect.
 */
export function returnPathFrom(state: unknown): string {
  const to =
    state && typeof state === "object" ? (state as { returnTo?: unknown }).returnTo : undefined;
  if (typeof to !== "string") return "/";
  if (!to.startsWith("/") || to.startsWith("//") || to.startsWith("/\\")) return "/";
  if (to.startsWith("/callback")) return "/";
  return to;
}

/** The current page, as `returnPathFrom` will accept it back. */
export function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}
